// Payload types + adapters for the B2C Personalized Plan endpoints
// (self-scoped twins of the tutor app's plan endpoints, same payload shape):
//   SAT: tests/digital/sat/personalizedPlan/
//   ACT: tests/digital/act/personalizedPlan/
//
// SAT and ACT payloads are normalized client-side into ONE generic PlanData so
// the components are driven by `meta` (sections, leverageMode) rather than being
// hardcoded to Reading & Writing / Math. The SAT backend payload is unchanged —
// adaptSat() maps it; the ACT endpoint already carries a `meta` block.
// adaptShsat stays as dormant plumbing for when B2C sells SHSAT.

export type TestKind = 'sat' | 'act' | 'shsat';
export type LeverageMode = 'scaled-points' | 'accuracy-gap';

export interface SectionConfig {
    key: string;      // 'rw'|'math' (SAT) or 'english'|'math'|'reading'|'science' (ACT)
    label: string;
    color: string;
    ceiling: number;  // 800 (SAT section) or 36 (ACT section)
}

export interface PlanMeta {
    testKind: TestKind;
    leverageMode: LeverageMode;
    sections: SectionConfig[];
    compositeKind: 'sum' | 'average';   // SAT total = sum; ACT composite = average
    compositeLabel: string;             // 'Total' | 'Composite'
    compositeCeiling: number;           // 1600 | 36
    compositeKeys: string[];            // section keys that feed the composite (ACT excludes Science)
    hasStem: boolean;
    // SHSAT only: 'percent' → show the level as raw "X / N" but plot trends and
    // deltas as percentage (the 100-q and 114-q forms aren't comparable raw).
    trendUnit?: 'value' | 'percent';
}

export interface PlanTest {
    studentTestId: string;
    name: string;
    date: string;                                 // YYYY-MM-DD
    sectionScores: Record<string, number | null>; // keyed by section key
    composite: number | null;                     // SAT total, ACT composite, SHSAT raw total
    stem: number | null;                          // ACT only
    sectionMax?: Record<string, number | null>;   // SHSAT: per-section question count (50/57)
    compositeMax?: number | null;                 // SHSAT: total question count (100/114)
}

export interface PlanDomain {
    domain: string;
    section: string;
    color: string;
    values: (number | null)[];   // per-test accuracy 0..1, aligned to tests[]
}

export interface PlanSkill {
    skill: string;
    domain: string;
    section: string;
    color: string;
    pct: number;        // lifetime accuracy 0..100
    qPerTest: number;
    attempted: number;
    correct: number;
    values: (number | null)[];
    ptUpsideMax?: number;        // scaled-points leverage (SAT + ACT)
    ptUpsideExpected?: number;   // scaled-points leverage (SAT + ACT)
    qMissedPerTest?: number;     // accuracy-gap leverage (SHSAT)
}

export interface PlanSnapshot {
    composite: number | null;    // SAT currentTotal; ACT currentComposite; SHSAT raw total
    compositeMax: number | null; // SHSAT: latest test's total question count (100/114)
    stem: number | null;         // ACT only
    perSection: Record<string, { score: number | null; date: string | null; max?: number | null }>;
    leverageTotal: number;       // SAT/ACT Σ top upside (ACT is composite-scale, ÷3); SHSAT Σ top questions-missed
}

export interface DomainRollup {
    domain: string;
    section: string;
    color: string;
    q: number;
    acc: number;
    pts?: number;      // scaled-points (SAT + ACT) — points on the table
    ptsMax?: number;   // scaled-points (SAT + ACT) — full bar
    qMissed?: number;  // accuracy-gap (SHSAT) — questions missed per test
}

export interface PlanData {
    student: { name: string };
    meta: PlanMeta;
    snapshot: PlanSnapshot | null;   // null = no qualifying tests of this kind
    tests: PlanTest[];
    domains: PlanDomain[];
    skills: PlanSkill[];
    top: PlanSkill[];
    domainRollup: DomainRollup[];
}

const SAT_SECTIONS: SectionConfig[] = [
    { key: 'rw', label: 'Reading & Writing', color: '#0575a2', ceiling: 800 },
    { key: 'math', label: 'Math', color: '#a23205', ceiling: 800 },
];

// Normalize the (unchanged) SAT endpoint payload into the generic model.
export function adaptSat(raw: any): PlanData {
    const meta: PlanMeta = {
        testKind: 'sat', leverageMode: 'scaled-points', sections: SAT_SECTIONS,
        compositeKind: 'sum', compositeLabel: 'Total', compositeCeiling: 1600,
        compositeKeys: ['rw', 'math'], hasStem: false,
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: { rw: t.scoreRW, math: t.scoreM },
        composite: t.total, stem: null,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentTotal, compositeMax: null, stem: null,
        perSection: {
            rw: { score: raw.snapshot.currentRW, date: raw.snapshot.currentRWDate },
            math: { score: raw.snapshot.currentMath, date: raw.snapshot.currentMathDate },
        },
        leverageTotal: raw.snapshot.totalTopUpside,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

// Normalize the ACT endpoint payload (carries its own `meta`) into the model.
export function adaptAct(raw: any): PlanData {
    const m = raw.meta || {};
    const meta: PlanMeta = {
        testKind: 'act', leverageMode: m.leverageMode || 'accuracy-gap',
        sections: m.sections || [],
        compositeKind: 'average', compositeLabel: 'Composite',
        compositeCeiling: m.ceiling || 36,
        compositeKeys: m.compositeKeys || ['english', 'math', 'reading'], hasStem: true,
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: t.sectionScores || {}, composite: t.composite, stem: t.stem,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentComposite, compositeMax: null, stem: raw.snapshot.currentSTEM,
        perSection: raw.snapshot.perSection || {},
        leverageTotal: raw.snapshot.totalTopUpside,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

// SHSAT (English Language Arts + Mathematics) — 2-level INTERNAL taxonomy like
// SAT, but raw-correct scoring + accuracy-gap leverage like ACT. The backend
// sends a full meta block; these are only fallbacks if it's missing.
const SHSAT_SECTIONS: SectionConfig[] = [
    { key: 'ela', label: 'ELA', color: '#0575a2', ceiling: 50 },
    { key: 'math', label: 'Math', color: '#a23205', ceiling: 50 },
];

// Normalize the SHSAT endpoint payload (carries its own `meta`) into the model.
export function adaptShsat(raw: any): PlanData {
    const m = raw.meta || {};
    const meta: PlanMeta = {
        testKind: 'shsat', leverageMode: m.leverageMode || 'accuracy-gap',
        sections: m.sections || SHSAT_SECTIONS,
        compositeKind: m.compositeKind || 'sum',
        compositeLabel: m.compositeLabel || 'Total',
        compositeCeiling: m.compositeCeiling || 100,
        compositeKeys: m.compositeKeys || ['ela', 'math'], hasStem: false,
        trendUnit: m.trendUnit || 'percent',
    };
    const tests: PlanTest[] = (raw.tests || []).map((t: any) => ({
        studentTestId: t.studentTestId, name: t.name, date: t.date,
        sectionScores: t.sectionScores || {}, composite: t.composite, stem: null,
        sectionMax: t.sectionMax || {}, compositeMax: t.compositeMax ?? null,
    }));
    const snapshot: PlanSnapshot | null = raw.snapshot ? {
        composite: raw.snapshot.currentComposite, compositeMax: raw.snapshot.currentCompositeMax ?? null, stem: null,
        perSection: raw.snapshot.perSection || {},
        leverageTotal: raw.snapshot.totalTopQMissed,
    } : null;
    return {
        student: raw.student, meta, snapshot, tests,
        domains: raw.domains || [], skills: raw.skills || [],
        top: raw.top || [], domainRollup: raw.domainRollup || [],
    };
}

export const fmtDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: 'UTC',
    });
};
